// The Starchive intake card: 5" x 7" index card, portrait, hole punched top centre (no printed punch guide; the top inch is left clear).
// Same markup for the on-screen preview (scaled) and the print route (1:1). Matches Starchive-Intake-Card-5x7-template.html.
import { LABEL } from '../lib/types'
import type { Condition, Ownership } from '../lib/types'

export interface CardData {
  how: 'bought' | 'consigned' | 'traded'
  date: string
  id: string
  from: string
  deal: string
  whose: Ownership | ''
  sentence: string
  what: string
  cond: Condition | ''
  intakeNo: number | string
  tradeNo?: number | null
  authenticated?: boolean
  measurements?: string
}

const Opt = ({ label, on, w }: { label: string; on: boolean; w?: string }) => (
  <span className={'ic-opt' + (on ? ' on' : '')} style={w ? { width: w } : undefined}><i className="ic-box" />{label}</span>
)

/** Split text over n dotted lines, roughly by character budget (the print route re-measures nothing; keep it simple). */
export function lines(text: string, n: number, perLine: number) {
  const words = (text || '').split(/\s+/).filter(Boolean)
  const out: string[] = []
  let cur = ''
  for (const w of words) {
    if ((cur + ' ' + w).trim().length > perLine && cur && out.length < n - 1) { out.push(cur); cur = w } else cur = (cur + ' ' + w).trim()
  }
  out.push(cur)
  while (out.length < n) out.push('')
  return out.slice(0, n)
}

export function IntakeCard({ c }: { c: CardData }) {
  const no = typeof c.intakeNo === 'number' ? (c.intakeNo > 0 ? String(c.intakeNo).padStart(4, '0') : '----') : c.intakeNo
  return (
    <div className="ic">
      <div className="ic-head">
        <img alt="Starchive" src="/brand/wordmark-black.png" />
        <div className="ic-head-r"><div className="ic-t">INTAKE CARD</div><div className="ic-no">INTAKE #<b>{no}</b>{c.tradeNo ? <span> · TRADE #{c.tradeNo}</span> : null}</div></div>
      </div>
      <div className="ic-rows">
        <div className="ic-row">
          <div className="ic-cell"><div className="ic-lbl">How it came in</div>
            <div className="ic-opts"><Opt label="Bought" on={c.how === 'bought'} w="0.78in" /><Opt label="Consigned" on={c.how === 'consigned'} w="0.9in" /><Opt label="Traded" on={c.how === 'traded'} /></div></div>
          <div className="ic-cell side"><div className="ic-lbl">Date</div><div className="ic-val">{c.date}</div></div>
        </div>
        <div className="ic-row">
          <div className="ic-cell"><div className="ic-lbl">Who it came from</div><div className="ic-val">{c.from}</div></div>
          <div className="ic-cell side"><div className="ic-lbl">Employee ID</div><div className="ic-val">{c.id}</div></div>
        </div>
        <div className="ic-row"><div className="ic-cell"><div className="ic-lbl">Cost or deal</div><div className="ic-val">{c.deal}</div></div></div>
        <div className="ic-row"><div className="ic-cell"><div className="ic-lbl">Whose piece</div>
          <div className="ic-opts">{(['shane', 'masih', 'split', 'consignor'] as Ownership[]).map((w) => <Opt key={w} label={LABEL.whose[w]} on={c.whose === w} w="0.9in" />)}</div></div></div>
        <div className="ic-row tall"><div className="ic-cell"><div className="ic-lbl">If consigned or traded, the deal in a sentence</div>
          <div className="ic-lines">{lines(c.sentence, 2, 62).map((l, i) => <div key={i}>{l}</div>)}</div></div></div>
        <div className="ic-row tall"><div className="ic-cell"><div className="ic-lbl">What it is — brand and item</div>
          <div className="ic-lines">{lines(c.what, 2, 58).map((l, i) => <div key={i}>{l}</div>)}</div></div></div>
        <div className="ic-row"><div className="ic-cell"><div className="ic-lbl">Authenticated by Christian</div>
          <div className="ic-opts"><Opt label="Yes" on={!!c.authenticated} w="0.9in" /></div></div></div>
        <div className="ic-row"><div className="ic-cell"><div className="ic-lbl">Condition</div>
          <div className="ic-opts"><Opt label="New w/ tags" on={c.cond === 'nwt'} w="1.12in" /><Opt label="Excellent" on={c.cond === 'excellent'} w="1.0in" /><Opt label="Good" on={c.cond === 'good'} w="0.88in" /><Opt label="Fair" on={c.cond === 'fair'} /></div></div></div>
        <div className="ic-row tall"><div className="ic-cell"><div className="ic-lbl">Measurements</div><div className="ic-lines">{lines(c.measurements ?? '', 2, 62).map((l, i) => <div key={i}>{l}</div>)}</div></div></div>
        <div className="ic-row tick"><div className="ic-cell"><div className="ic-lbl">Tick as you finish</div>
          <div className="ic-opts"><Opt label="Steamed" on={false} w="1.2in" /><Opt label="Measured" on={false} w="1.2in" /><Opt label="Listed" on={false} w="1.2in" /></div>
          <div className="ic-opts"><Opt label="Photographed" on={false} w="1.2in" /><Opt label="On the floor" on={false} w="1.2in" /></div></div></div>
      </div>
    </div>
  )
}

/** Card CSS, shared by the preview and the print page. Sizes are in inches so the print is exact. */
export const CARD_CSS = `
.ic { position: relative; width: 5in; height: 7in; padding: 0 0.28in; overflow: hidden; background: #fff; color: #000; font-family: Helvetica, Arial, sans-serif; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.ic *, .ic *::before, .ic *::after { box-sizing: border-box; }
.ic-head { position: absolute; top: 0.74in; left: 0.28in; right: 0.28in; height: 0.72in; display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 1.4pt solid #222; padding-bottom: 0.1in; }
.ic-head img { width: 1.45in; display: block; }
.ic-head-r { text-align: right; }
.ic-t { font-weight: bold; font-size: 8pt; letter-spacing: 2.2pt; }
.ic-no { font-weight: bold; font-size: 6.3pt; letter-spacing: 1.1pt; color: #8a8a8a; margin-top: 3pt; }
.ic-no b { color: #000; font-size: 9pt; letter-spacing: 0.5pt; margin-left: 3pt; }
.ic-no span { color: #000; margin-left: 3pt; }
.ic-rows { position: absolute; top: 1.48in; left: 0.28in; right: 0.28in; }
.ic-row { display: flex; border-bottom: 0.8pt solid #222; height: 0.44in; }
.ic-row.tall { height: 0.66in; }
.ic-row.tick { height: 0.6in; border-bottom: 1.4pt solid #222; }
.ic-cell { flex: 1; padding-top: 0.06in; position: relative; overflow: hidden; }
.ic-cell.side { flex: 0 0 34%; border-left: 0.8pt solid #222; padding-left: 8pt; }
.ic-lbl { font-weight: bold; font-size: 6.3pt; letter-spacing: 1.1pt; color: #8a8a8a; text-transform: uppercase; line-height: 1; }
.ic-val { font-size: 10pt; font-weight: bold; margin-top: 0.05in; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-height: 12pt; }
.ic-opts { display: flex; margin-top: 0.06in; font-weight: bold; font-size: 8.6pt; }
.ic-opts + .ic-opts { margin-top: 0.05in; }
.ic-opt { display: flex; align-items: center; }
.ic-box { width: 7.5pt; height: 7.5pt; border: 0.8pt solid #000; margin-right: 3.5pt; position: relative; flex: 0 0 auto; display: inline-block; }
.ic-opt.on .ic-box { background: #000; }
.ic-opt.on .ic-box::after { content: ""; position: absolute; left: 2.2pt; top: 0.2pt; width: 2pt; height: 4.3pt; border: solid #fff; border-width: 0 1.1pt 1.1pt 0; transform: rotate(45deg); }
.ic-lines { position: absolute; left: 0; right: 0; top: 0.17in; }
.ic-lines div { height: 0.21in; border-bottom: 0.5pt dotted #bbb; font-weight: bold; font-size: 9.5pt; line-height: 0.19in; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding-top: 0.02in; }
`

export const todayCard = () => new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit', timeZone: 'America/New_York' })
